import React, { useEffect, useState } from "react";
import { Title } from "@jasper/shared/stories/Title/Title";
import { ButtonRadioSelect } from "@jasper/shared/stories/ButtonRadioSelect/ButtonRadioSelect";
import { Box, Select } from "@mantine/core";
import {
  filterShadeByShadeType,
  getShadeBrandByShade,
} from "@jasper/shared/utils/item.utils";
import { FixedProsthesisShadeType } from "@jasper/shared/types/enums";
import {
  getGingivaShadeFormChoices,
  getShadeTypeFormChoices,
} from "@jasper/shared/utils/product.utils";
import { Controller, useFormContext } from "react-hook-form";
import { Product } from "@jasper/shared";

export const MaterialAndShadeSelection: React.FC<{
  removableItemChoicesData: any;
  product: Product;
}> = ({ removableItemChoicesData, product }) => {
  const [selectedShadeList, setSelectedShadeList] = useState<
    { value: string; label: string }[]
  >([]);

  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();
  const selectedShadeBrand = watch("brand");
  const selectedShade = watch("shade");
  useEffect(() => {
    const list = filterShadeByShadeType(
      removableItemChoicesData?.getItemShadesWhere ?? [],
      selectedShadeBrand as FixedProsthesisShadeType
    );
    setSelectedShadeList(list);

    if (!list.some(shade => shade.value === selectedShade)) {
      setValue("shade", null);
    }
  }, [selectedShadeBrand, selectedShade]);

  useEffect(() => {
    if (product?.removableItem[0]?.itemMaterial) {
      setValue("material", product.removableItem[0].itemMaterial.id);
    }
    if (product?.removableItem[0]?.teethShade) {
      const shadeBrand = getShadeBrandByShade(
        removableItemChoicesData?.getItemShadesWhere ?? [],
        product.removableItem[0].teethShade.id
      );
      setValue("brand", shadeBrand);
      setValue("shade", product.removableItem[0].teethShade.id);
    }
    if (product?.removableItem[0]?.gingivaShade) {
      setValue("gingivaShade", product.removableItem[0].gingivaShade.id);
    }
  }, [product]);

  return (
    <Box pt="lg">
      <Title
        title="Matériau et teinte - prothèse amovible partielle"
        size={3}
        mb="md"
      />
      <Controller
        control={control}
        name="material"
        rules={{ required: "Veuillez sélectionner un matériau" }}
        render={({ field }) => (
          <ButtonRadioSelect
            options={removableItemChoicesData.getItemMaterialsWhere.map(
              (item: any) => ({
                label: item.label,
                value: item.id,
              })
            )}
            setSelected={value => field.onChange(value as string)}
            selected={field.value}
            label="Matériau"
          />
        )}
      />
      {errors.material && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.material.message as string}
        </span>
      )}
      <Controller
        control={control}
        name="brand"
        rules={{ required: "Veuillez sélectionner une marque de teintier" }}
        render={({ field }) => (
          <ButtonRadioSelect
            options={getShadeTypeFormChoices(removableItemChoicesData)}
            setSelected={value => field.onChange(value as string)}
            selected={field.value}
            label="Type de teintier"
          />
        )}
      />
      {errors.brand && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.brand.message as string}
        </span>
      )}
      <Title
        title="Teinte des dents"
        size={5}
      />
      <Controller
        control={control}
        name="shade"
        rules={{ required: "Veuillez sélectionner une teinte" }}
        render={({ field }) => (
          <Select
            ref={field.ref}
            my="xs"
            data-testid="select-teinte"
            searchable
            clearable
            placeholder="Selectionnez une teinte"
            data={selectedShadeList}
            onChange={value => field.onChange(value as string)}
            value={field.value}
          />
        )}
      />
      {errors.shade && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.shade.message as string}
        </span>
      )}
      <Controller
        control={control}
        name="gingivaShade"
        rules={{ required: "Veuillez sélectionner une teinte de gencive" }}
        render={({ field }) => (
          <ButtonRadioSelect
            options={getGingivaShadeFormChoices(removableItemChoicesData)}
            setSelected={value => field.onChange(value as string)}
            selected={field.value}
            label="Teinte de gencive"
          />
        )}
      />
      {errors.gingivaShade && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.gingivaShade.message as string}
        </span>
      )}
    </Box>
  );
};
